import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ArrowLeft, ShieldCheck, Loader2, Sparkles } from "lucide-react";

const BENEFITS = [
  "Unlimited syllabus uploads",
  "Multiple courses",
  "Full calendar & assignment tracking",
  "Grade calculator",
  "Suggested study plans",
  "Edit extracted events",
  "Saved account & permanent access",
];

export default function Upgrade() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    setLoading(true);
    try {
      const res = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-8">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </button>
        <div className="text-center mb-8">
          <Logo size="lg" className="justify-center mb-6" />
          <h1 className="text-3xl font-semibold text-slate-900">Pay once. Stay organized all semester.</h1>
          <p className="mt-2 text-slate-500">No monthly subscription. No recurring charges.</p>
        </div>

        {user?.paid_status ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="already-paid">
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
            <p className="font-semibold text-emerald-900">You already have lifetime access.</p>
            <Button onClick={() => navigate("/dashboard")} className="mt-5 bg-slate-900 hover:bg-slate-800 rounded-lg">Go to dashboard</Button>
          </div>
        ) : (
          <div className="rounded-2xl bg-white border border-slate-200 shadow-lg p-8">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Syllabify Lifetime Access</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-5xl font-bold text-slate-900">$4.99</span>
                  <span className="text-slate-500">one-time</span>
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
                <Sparkles className="h-3.5 w-3.5" /> Lifetime
              </span>
            </div>
            <ul className="mt-6 space-y-3">
              {BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" /> {b}
                </li>
              ))}
            </ul>
            <Button onClick={checkout} disabled={loading} data-testid="checkout-lifetime-btn"
              className="mt-8 w-full bg-blue-600 hover:bg-blue-700 py-6 rounded-lg text-base font-medium">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Unlock Syllabify — $4.99"}
            </Button>
            <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-slate-400">
              <ShieldCheck className="h-4 w-4" /> Secure one-time payment via Stripe. No subscription.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
